<template>
  <div class="collection-page">
    <Teleport to="body">
      <ItemQuickPanel
        :visible="isItemModalVisible"
        :itemId="selectedItemKey"
        :create-mode="creatingItem"
        @close="closeItemDetails"
        @saved-item="savedItem"
        @open-offer="
          (key) =>
            openDetail('purchase', key, {
              from: 'item',
              fromId: selectedItemKey,
            })
        "
        @open-sale="
          (key) =>
            openDetail('sale', key, { from: 'item', fromId: selectedItemKey })
        "
        @open-package="openPackage"
      />
    </Teleport>

    <header class="page-heading">
      <div>
        <p>Inventaire</p>
        <h1>Collection</h1>
        <span>Retrouvez, complétez et préparez vos objets pour la vente.</span>
      </div>
      <button type="button" class="primary-action" @click="openCreateItem">
        ＋ Ajouter un objet
      </button>
    </header>

    <section class="summary" aria-label="Synthèse de la collection chargée">
      <div>
        <strong>{{ totalItems }}</strong
        ><span>Objets au total</span>
      </div>
      <div>
        <strong>{{ availableItemsCount }}</strong
        ><span>En collection chargés</span>
      </div>
      <div>
        <strong>{{ soldItemsCount }}</strong
        ><span>Vendus chargés</span>
      </div>
      <div>
        <strong>{{ incompleteItemsCount }}</strong
        ><span>À compléter chargés</span>
      </div>
      <p>Les répartitions portent sur les objets actuellement chargés.</p>
    </section>

    <section class="collection-panel">
      <nav class="tabs" aria-label="Vues de la collection">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          type="button"
          :class="{ active: filters.sold === tab.value }"
          @click="setStatusFilter(tab.value)"
        >
          {{ tab.label }}
        </button>
      </nav>

      <div class="toolbar">
        <label class="search-field">
          <span class="sr-only">Rechercher dans la collection</span>
          <i class="fas fa-search" aria-hidden="true"></i>
          <input
            v-model="filters.name"
            type="search"
            placeholder="Rechercher un objet…"
            @input="debouncedFilterItems"
          />
        </label>
        <select
          v-model="filters.type"
          aria-label="Filtrer par type"
          @change="filterItems"
        >
          <option value="">Tous les types</option>
          <option value="console">Consoles</option>
          <option value="game">Jeux</option>
          <option value="device">Périphériques</option>
        </select>
        <select
          v-model="filters.platform"
          aria-label="Filtrer par plateforme"
          @change="filterItems"
        >
          <option value="">Toutes les plateformes</option>
          <option
            v-for="platform in platforms"
            :key="platform.name"
            :value="platform.name"
          >
            {{ platform.name }}
          </option>
        </select>
        <button
          v-if="hasActiveFilters"
          type="button"
          class="reset"
          @click="clearFilters"
        >
          Réinitialiser
        </button>
      </div>

      <div v-if="selectedKeys.length" class="selection-bar" role="status">
        <div>
          <strong>{{ selectedKeys.length }}</strong>
          objet{{ selectedKeys.length > 1 ? "s" : "" }} sélectionné{{
            selectedKeys.length > 1 ? "s" : ""
          }}
        </div>
        <button
          type="button"
          class="selection-primary"
          @click="createSaleFromSelection"
        >
          Créer une vente
        </button>
        <button
          v-if="selectedKeys.length === 1"
          type="button"
          @click="showItemDetails(selectedKeys[0])"
        >
          Ouvrir la fiche
        </button>
        <button type="button" @click="selectedKeys = []">
          Effacer la sélection
        </button>
      </div>

      <div v-if="loading" class="loading-state">
        <LoaderDiv
          :isLoading="true"
          variant="modern"
          loaderText="Chargement de la collection…"
        />
      </div>

      <div v-else-if="!items.length" class="empty-state">
        <span>◎</span>
        <h2>Aucun objet trouvé</h2>
        <p>
          {{
            hasActiveFilters
              ? "Modifiez les filtres pour élargir les résultats."
              : "Ajoutez le premier objet de votre collection."
          }}
        </p>
        <button v-if="!hasActiveFilters" type="button" @click="openCreateItem">
          Ajouter un objet
        </button>
      </div>

      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="check-column">
                <input
                  type="checkbox"
                  :checked="allVisibleSelected"
                  aria-label="Sélectionner les objets affichés"
                  @change="toggleAllVisible"
                />
              </th>
              <th>Objet</th>
              <th>Type</th>
              <th>Provenance</th>
              <th>État</th>
              <th>Statut</th>
              <th><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in items"
              :key="item._key"
              :class="{ selected: selectedKeys.includes(item._key) }"
            >
              <td class="check-column">
                <input
                  v-model="selectedKeys"
                  type="checkbox"
                  :value="item._key"
                  :aria-label="`Sélectionner ${itemName(item)}`"
                />
              </td>
              <td>
                <button
                  type="button"
                  class="identity"
                  @click="showItemDetails(item._key)"
                >
                  <span class="thumb">
                    <img
                      v-if="item.photo || item.offer_id"
                      :src="getThumbnailUrl(item)"
                      :alt="itemName(item)"
                      loading="lazy"
                      @error="handleImageError"
                    />
                    <b v-else>{{ itemName(item).charAt(0).toUpperCase() }}</b>
                  </span>
                  <span>
                    <strong>{{ itemName(item) }}</strong>
                    <small
                      >{{ item.brand || "Marque non renseignée"
                      }}<template v-if="item.model">
                        · {{ item.model }}</template
                      ></small
                    >
                  </span>
                </button>
              </td>
              <td>
                <span class="type-pill">{{ typeLabel(item.type) }}</span>
              </td>
              <td>
                <button
                  v-if="item.offer_id"
                  type="button"
                  class="origin"
                  @click="showOfferDetails(item.offer_id)"
                >
                  #{{ item.offer_id }}
                </button>
                <span v-else class="muted">Ajout manuel</span>
              </td>
              <td>
                <span :class="['condition', { missing: !item.condition }]">{{
                  item.condition || "À renseigner"
                }}</span>
              </td>
              <td>
                <div class="sale-status">
                  <span :class="['status', item.sold ? 'sold' : 'available']">
                    <i></i>{{ item.sold ? "Vendu" : "En collection" }}
                  </span>
                  <small
                    v-if="item.sold && hasSoldPrice(item)"
                    class="sold-price"
                    >{{ formatSoldPrice(item.soldPrice) }}</small
                  >
                </div>
              </td>
              <td>
                <div class="row-actions">
                  <button type="button" @click="showItemDetails(item._key)">
                    Détails →
                  </button>
                  <button
                    type="button"
                    class="more"
                    :aria-label="`Supprimer ${itemName(item)}`"
                    @click="confirmDeleteItem(item)"
                  >
                    •••
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="loadingMore" class="load-more">
        Chargement d’objets supplémentaires…
      </div>
      <button
        v-else-if="!allItemsLoaded && items.length"
        type="button"
        class="load-more manual"
        @click="loadItems"
      >
        Charger plus d’objets
      </button>
      <p v-else-if="items.length" class="end-state">
        {{ items.length }} objet{{ items.length > 1 ? "s" : "" }} affiché{{
          items.length > 1 ? "s" : ""
        }}
      </p>
    </section>

    <Teleport to="body">
      <div
        v-if="itemToDelete"
        class="confirmation-layer"
        @click.self="cancelDelete"
      >
        <section
          class="confirmation"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="delete-title"
        >
          <span>Suppression</span>
          <h2 id="delete-title">Supprimer cet objet ?</h2>
          <p>
            <strong>{{ itemName(itemToDelete) }}</strong> sera définitivement
            supprimé de la collection.
          </p>
          <footer>
            <button type="button" @click="cancelDelete">Annuler</button>
            <button
              type="button"
              class="danger"
              :disabled="deleting"
              @click="deleteItem"
            >
              {{ deleting ? "Suppression…" : "Supprimer" }}
            </button>
          </footer>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "axios";
import config from "@/config/config.js";
import { isTokenExpired } from "../../auth";
import ItemQuickPanel from "./ItemQuickPanel.vue";
import { navigateFromDetail, openDetail } from "../../detailStack";
import LoaderDiv from "../LoaderDiv.vue";

export default {
  name: "ItemsList",
  components: { LoaderDiv, ItemQuickPanel },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const items = ref([]);
    const platforms = ref([]);
    const loading = ref(true);
    const loadingMore = ref(false);
    const currentPage = ref(0);
    const totalItems = ref(0);
    const allItemsLoaded = ref(false);
    const selectedKeys = ref([]);
    const selectedItemKey = ref(null);
    const isItemModalVisible = ref(false);
    const creatingItem = ref(false);
    const itemToDelete = ref(null);
    const deleting = ref(false);
    const filters = ref({
      offerId: "",
      type: "",
      sold: "",
      name: "",
      platform: "",
    });
    const limit = config.itemsLimit;
    const statusTabs = [
      { label: "Tous les objets", value: "" },
      { label: "En collection", value: "false" },
      { label: "Vendus", value: "true" },
    ];

    const soldItemsCount = computed(
      () => items.value.filter((item) => item.sold).length,
    );
    const availableItemsCount = computed(
      () => items.value.filter((item) => !item.sold).length,
    );
    const incompleteItemsCount = computed(
      () =>
        items.value.filter(
          (item) =>
            !item.condition || !(item.title || item.name) || !item.brand,
        ).length,
    );
    const hasActiveFilters = computed(() =>
      Object.values(filters.value).some(Boolean),
    );
    const allVisibleSelected = computed(
      () =>
        Boolean(items.value.length) &&
        items.value.every((item) => selectedKeys.value.includes(item._key)),
    );

    const authHeaders = () => ({
      Authorization: `Bearer ${localStorage.getItem("access_token")}`,
    });
    const checkAuth = () => {
      const token = localStorage.getItem("access_token");
      if (!isTokenExpired(token)) return true;
      router.push("/");
      return false;
    };
    const itemName = (item) => item?.name || item?.title || "Objet sans nom";
    const typeLabel = (type) =>
      ({ console: "Console", game: "Jeu", device: "Périphérique" })[type] ||
      "Objet";
    const hasSoldPrice = (item) =>
      item?.soldPrice !== null &&
      item?.soldPrice !== undefined &&
      item?.soldPrice !== "";
    const formatSoldPrice = (value) =>
      new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
      }).format(Number(value) || 0);
    // The item's own photo, else the photo of its lot
    const getThumbnailUrl = (item) =>
      item.photo
        ? `${config.cdnUrl}/images/${item._key}/${item.photo}`
        : item.offer_id
          ? `${config.cdnUrl}/images/${item.offer_id}/${item.offer_id}_thumbnail.jpg`
          : "";
    const handleImageError = (event) => {
      event.target.onerror = null;
      event.target.src =
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Crect width='80' height='80' fill='%23edf1f4' rx='6'/%3E%3Cpath d='M20 55 L30 42 L38 50 L48 36 L60 55Z' fill='%23b6c0ca'/%3E%3C/svg%3E";
    };

    let filterTimeout;
    const debouncedFilterItems = () => {
      clearTimeout(filterTimeout);
      filterTimeout = setTimeout(filterItems, 350);
    };
    // Only the answer to the latest request counts (search typed, filter
    // changed or scroll during a load): no duplicate, no page skipped
    let listRequest = 0,
      listBusy = false;
    const loadItems = async () => {
      if (allItemsLoaded.value || listBusy || !checkAuth()) return;
      listBusy = true;
      const request = ++listRequest;
      currentPage.value ? (loadingMore.value = true) : (loading.value = true);
      const params = new URLSearchParams({ page: currentPage.value, limit });
      Object.entries(filters.value).forEach(
        ([key, value]) => value && params.set(key, value),
      );
      try {
        const response = await axios.get(`${config.apiUrl}/items?${params}`);
        if (request !== listRequest) return;
        const result = response.data?.result || [];
        items.value.push(...result);
        totalItems.value = response.data?.total_items || 0;
        currentPage.value += 1;
        allItemsLoaded.value =
          items.value.length >= totalItems.value || !result.length;
      } catch (error) {
        if (request === listRequest)
          console.error("Impossible de charger la collection", error);
      } finally {
        if (request === listRequest) {
          loading.value = false;
          loadingMore.value = false;
          listBusy = false;
        }
      }
    };
    const filterItems = async () => {
      listRequest++; // the request in progress is for the previous filters
      listBusy = false;
      currentPage.value = 0;
      allItemsLoaded.value = false;
      items.value = [];
      selectedKeys.value = [];
      await loadItems();
    };
    const setStatusFilter = (value) => {
      if (filters.value.sold === value) return;
      filters.value.sold = value;
      filterItems();
    };
    const clearFilters = () => {
      filters.value = {
        offerId: "",
        type: "",
        sold: "",
        name: "",
        platform: "",
      };
      filterItems();
    };
    const createSaleFromSelection = () => {
      const selectedItems = items.value.filter((item) =>
        selectedKeys.value.includes(item._key),
      );
      sessionStorage.setItem(
        "prexens:sale-draft-items",
        JSON.stringify(selectedItems),
      );
      router.push({ path: "/sales", query: { create: "1" } });
    };
    const toggleAllVisible = () => {
      if (allVisibleSelected.value) {
        const visible = new Set(items.value.map((item) => item._key));
        selectedKeys.value = selectedKeys.value.filter(
          (key) => !visible.has(key),
        );
      } else {
        selectedKeys.value = [
          ...new Set([
            ...selectedKeys.value,
            ...items.value.map((item) => item._key),
          ]),
        ];
      }
    };
    // Sheets of the records an item refers to (lot, sale) open over the
    // Collection; the item stays open below (see detailStack.js)
    const showOfferDetails = (offerId) => openDetail("purchase", offerId);
    const openPackage = (packageId) =>
      navigateFromDetail(`/packages/${packageId}`, {
        pagePanel: selectedItemKey.value
          ? { type: "item", id: selectedItemKey.value }
          : null,
      });
    const showItemDetails = (key) => {
      creatingItem.value = false;
      selectedItemKey.value = key;
      isItemModalVisible.value = true;
    };
    const closeItemDetails = () => {
      isItemModalVisible.value = false;
      selectedItemKey.value = null;
      creatingItem.value = false;
    };
    const openCreateItem = () => {
      selectedItemKey.value = null;
      creatingItem.value = true;
      isItemModalVisible.value = true;
    };
    // An item saved in a sheet opened elsewhere: updated in the list
    const onChanged = (event) => {
      const { type, record } = event.detail || {};
      if (type !== "item" || !record?._key) return;
      const index = items.value.findIndex(
        (entry) => entry._key === record._key,
      );
      if (index >= 0)
        items.value.splice(index, 1, { ...items.value[index], ...record });
    };
    const savedItem = (item) => {
      const wasCreating = creatingItem.value;
      const index = items.value.findIndex((entry) => entry._key === item._key);
      const normalized = {
        ...item,
        title: item.title || item.name || "Sans titre",
      };
      if (index >= 0)
        items.value.splice(index, 1, { ...items.value[index], ...normalized });
      else {
        items.value.unshift(normalized);
        totalItems.value += 1;
      }
      if (wasCreating) closeItemDetails();
    };
    const confirmDeleteItem = (item) => {
      itemToDelete.value = item;
    };
    const cancelDelete = () => {
      if (!deleting.value) itemToDelete.value = null;
    };
    const deleteItem = async () => {
      if (!itemToDelete.value) return;
      deleting.value = true;
      try {
        await axios.delete(
          `${config.apiUrl}/items/${itemToDelete.value._key}`,
          { headers: authHeaders() },
        );
        const key = itemToDelete.value._key;
        items.value = items.value.filter((item) => item._key !== key);
        selectedKeys.value = selectedKeys.value.filter(
          (selected) => selected !== key,
        );
        totalItems.value = Math.max(0, totalItems.value - 1);
        itemToDelete.value = null;
      } catch (error) {
        console.error("Impossible de supprimer l’objet", error);
      } finally {
        deleting.value = false;
      }
    };
    const onScroll = () => {
      if (
        window.scrollY + window.innerHeight >=
        document.documentElement.scrollHeight - 500
      )
        loadItems();
    };

    onMounted(async () => {
      if (route.query.offerId)
        filters.value.offerId = String(route.query.offerId);
      try {
        const response = await axios.get(`${config.apiUrl}/lists/platforms`, {
          headers: authHeaders(),
        });
        platforms.value = response.data || [];
      } catch (error) {
        console.error("Impossible de charger les plateformes", error);
      }
      await loadItems();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("prexens:changed", onChanged);
    });
    onUnmounted(() => {
      clearTimeout(filterTimeout);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("prexens:changed", onChanged);
    });

    return {
      openDetail,
      openPackage,
      allItemsLoaded,
      allVisibleSelected,
      availableItemsCount,
      cancelDelete,
      clearFilters,
      closeItemDetails,
      confirmDeleteItem,
      createSaleFromSelection,
      debouncedFilterItems,
      deleteItem,
      deleting,
      filterItems,
      filters,
      formatSoldPrice,
      getThumbnailUrl,
      handleImageError,
      hasSoldPrice,
      hasActiveFilters,
      incompleteItemsCount,
      creatingItem,
      isItemModalVisible,
      itemName,
      items,
      itemToDelete,
      loadItems,
      loading,
      loadingMore,
      openCreateItem,
      platforms,
      savedItem,
      selectedItemKey,
      selectedKeys,
      setStatusFilter,
      showItemDetails,
      showOfferDetails,
      soldItemsCount,
      statusTabs,
      toggleAllVisible,
      totalItems,
      typeLabel,
    };
  },
};
</script>

<style scoped>
.collection-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e2e7ed;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
}
.page-heading,
.summary,
.collection-panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action {
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #172235;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}
.summary {
  display: flex;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.summary div {
  display: flex;
  min-width: 160px;
  flex-direction: column;
  padding: 16px 20px;
  border-right: 1px solid var(--line);
}
.summary strong {
  font-size: 20px;
}
.summary span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 11px;
}
.summary p {
  align-self: center;
  margin: 0 20px 0 auto;
  color: #87919f;
  font-size: var(--meta-size);
}
.collection-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.tabs {
  display: flex;
  gap: 25px;
  height: 53px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
}
.tabs button {
  position: relative;
  border: 0;
  background: none;
  color: #778294;
  font-size: 12px;
  font-weight: 700;
}
.tabs button.active {
  color: var(--text);
}
.tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--green);
  content: "";
}
.toolbar {
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #fbfcfd;
}
.search-field {
  display: flex;
  width: min(430px, 100%);
  height: 38px;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #8993a1;
}
.search-field input {
  width: 100%;
  border: 0;
  outline: 0;
  font-size: 12px;
}
.toolbar select {
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #4e5a6c;
  font-size: 12px;
}
.reset {
  border: 0;
  background: none;
  color: #667285;
  font-size: 11px;
  font-weight: 700;
}
.selection-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border-bottom: 1px solid #bde0d3;
  background: #edf8f4;
  color: #275f4e;
  font-size: 12px;
}
.selection-bar div {
  margin-right: auto;
}
.selection-bar strong {
  margin-right: 4px;
}
.selection-bar .selection-primary {
  border-color: #172235;
  background: #172235;
  color: #fff;
}
.selection-bar button {
  height: 31px;
  padding: 0 10px;
  border: 1px solid #b9d9ce;
  border-radius: 6px;
  background: #fff;
  color: #2e6754;
  font-size: var(--meta-size);
  font-weight: 700;
}
.table-wrap {
  overflow-x: auto;
}
.collection-panel table {
  width: 100%;
  border-collapse: collapse;
}
.collection-panel th {
  padding: 11px 14px;
  border-bottom: 1px solid var(--line);
  background: #fff;
  color: #7d8796;
  font-size: var(--meta-size);
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
.collection-panel td {
  padding: 12px 14px;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
}
.collection-panel tbody tr:hover,
.collection-panel tbody tr.selected {
  background: #f7fbf9;
}
.check-column {
  width: 36px !important;
  padding-right: 0 !important;
}
.identity {
  display: flex;
  min-width: 310px;
  align-items: center;
  gap: 11px;
  border: 0;
  background: none;
  text-align: left;
}
.thumb {
  display: grid;
  width: 48px;
  height: 48px;
  flex: 0 0 48px;
  overflow: hidden;
  border: 1px solid #e1e6ec;
  border-radius: 7px;
  background: #f0f3f5;
}
.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.thumb b {
  margin: auto;
  color: #6d7a8b;
  font-size: 14px;
}
.identity > span:last-child {
  min-width: 0;
}
.identity strong,
.identity small {
  display: block;
}
.identity strong {
  max-width: 390px;
  overflow: hidden;
  color: #273244;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity small {
  margin-top: 4px;
  color: #8993a1;
  font-size: var(--meta-size);
}
.type-pill {
  padding: 5px 7px;
  border-radius: 6px;
  background: #f0f3f6;
  color: #596779;
  font-size: var(--meta-size);
  font-weight: 700;
}
.origin {
  border: 0;
  background: none;
  color: #53708d;
  font-size: var(--meta-size);
}
.muted {
  color: #8993a1;
  font-size: var(--meta-size);
}
.condition {
  color: #4c596b;
  font-size: 11px;
}
.condition.missing {
  color: #b36b26;
}
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: var(--meta-size);
  font-weight: 700;
}
.status i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: currentColor;
}
.status.available {
  background: #eaf7f2;
  color: #21755c;
}
.status.sold {
  background: #eef3f8;
  color: #4e6680;
}
.sale-status {
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 5px;
}
.sold-price {
  color: #354153;
  font-size: var(--meta-size);
  font-weight: 750;
  white-space: nowrap;
}
.row-actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}
.row-actions button {
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d7dee6;
  border-radius: 7px;
  background: #fff;
  color: #566376;
  font-size: var(--meta-size);
  font-weight: 700;
}
.row-actions .more {
  width: 34px;
  padding: 0;
}
.loading-state,
.empty-state {
  min-height: 340px;
}
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}
.empty-state > span {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: 12px;
  background: #eaf7f2;
  color: var(--green);
}
.empty-state h2 {
  margin: 15px 0 6px;
  font-size: 17px;
}
.empty-state p {
  margin: 0;
  color: var(--muted);
  font-size: 12px;
}
.empty-state button {
  margin-top: 15px;
  padding: 10px 13px;
  border: 0;
  border-radius: 7px;
  background: #172235;
  color: #fff;
}
.load-more,
.end-state {
  display: block;
  width: 100%;
  margin: 0;
  padding: 15px;
  border: 0;
  border-top: 1px solid #edf0f4;
  background: #fff;
  color: #7b8796;
  text-align: center;
  font-size: 11px;
}
.load-more.manual {
  color: #39644f;
  font-weight: 700;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
.confirmation-layer {
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: grid;
  place-items: center;
  padding: calc(20px + var(--safe-top)) 20px calc(20px + var(--safe-bottom));
  background: rgba(15, 23, 36, 0.5);
}
.confirmation {
  width: min(420px, 100%);
  padding: 25px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 24px 70px rgba(15, 23, 36, 0.25);
}
.confirmation > span {
  color: #b64742;
  font-size: var(--meta-size);
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.confirmation h2 {
  margin: 7px 0 10px;
  font-size: 20px;
}
.confirmation p {
  margin: 0;
  color: #687486;
  font-size: 12px;
  line-height: 1.6;
}
.confirmation footer {
  display: flex;
  justify-content: flex-end;
  gap: 9px;
  margin-top: 22px;
}
.confirmation button {
  height: 38px;
  padding: 0 14px;
  border: 1px solid #d4dbe3;
  border-radius: 7px;
  background: #fff;
  color: #596577;
  font-size: 11px;
  font-weight: 700;
}
.confirmation .danger {
  border-color: #ba4d48;
  background: #ba4d48;
  color: #fff;
}
@media (max-width: 900px) {
  .collection-page {
    padding: 22px 14px;
  }
  .summary p {
    display: none;
  }
  .summary div {
    min-width: 0;
    flex: 1;
    padding: 13px;
  }
  .summary div:nth-child(4) {
    display: none;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .search-field {
    width: 100%;
  }
  .collection-panel th:nth-child(4),
  .collection-panel td:nth-child(4),
  .collection-panel th:nth-child(5),
  .collection-panel td:nth-child(5) {
    display: none;
  }
}
@media (max-width: 620px) {
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .primary-action {
    width: 100%;
  }
  .summary div:nth-child(2),
  .summary div:nth-child(3) {
    display: none;
  }
  .tabs {
    gap: 16px;
    overflow-x: auto;
  }
  .toolbar {
    flex-direction: column;
  }
  .toolbar select {
    width: 100%;
  }
  .collection-panel th:nth-child(3),
  .collection-panel td:nth-child(3) {
    display: none;
  }
  .identity {
    min-width: 0;
  }
  /* Phone: the row opens the item already ("Détails" twice), and the
     delete menu is hidden on small screens: no actions column */
  .collection-panel th:last-child,
  .collection-panel td:last-child {
    display: none;
  }
  .selection-bar {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .selection-bar div {
    width: 100%;
  }
  .row-actions .more {
    display: none;
  }
}
</style>
